import React, { useState, useEffect, useCallback, useRef } from "react";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import {
  Box, Typography, Stack, Tooltip, IconButton, CircularProgress,
  Alert, Dialog, DialogContent, DialogTitle, Drawer, List, ListItem,
  ListItemText, ListItemAvatar, Avatar, Switch, Divider,
} from "@mui/material";
import {
  Inbox, WidgetsOutlined, Close, OpenInFull, DragIndicator, EditOutlined,
} from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";

const API_URL = process.env.REACT_APP_CLIENT_API_URL;
const COLS    = 12;
const ROW_H   = 60;
const MARGIN  = [6, 6];

// ── Misura la larghezza del container (esclude scrollbar) ────────────────────
function useContainerWidth(ref) {
  // Inizializza con window.innerWidth per evitare flash con canvasWidth=0
  const [width, setWidth] = useState(() => window.innerWidth || 1200);
  useEffect(() => {
    if (!ref.current) return;
    const measure = () => {
      const w = ref.current.clientWidth;
      if (w > 0) setWidth(w);
    };
    const ro = new ResizeObserver(measure);
    ro.observe(ref.current);
    measure();
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

// CSS per tutti gli handle di resize — invisibili, solo cursore bidirezionale.
// Le handle coprono l'intera area del bordo/angolo per un'interazione naturale.
const RESIZE_HANDLE_CSS = `
  .react-resizable-handle {
    position: absolute;
    background: transparent;
    z-index: 20;
    pointer-events: all;
  }
  /* Angoli: 18×18 px agli angoli esatti */
  .react-resizable-handle-se { bottom: 0; right: 0;  width: 18px; height: 18px; cursor: se-resize; }
  .react-resizable-handle-sw { bottom: 0; left: 0;   width: 18px; height: 18px; cursor: sw-resize; }
  .react-resizable-handle-ne { top: 0;    right: 0;  width: 18px; height: 18px; cursor: ne-resize; }
  .react-resizable-handle-nw { top: 0;    left: 0;   width: 18px; height: 18px; cursor: nw-resize; }
  /* Bordi: striscia sottile piena lungo tutto il lato (con gap agli angoli) */
  .react-resizable-handle-n  { top: 0;    left: 18px; right: 18px; height: 6px;  width: auto; cursor: n-resize; transform: none; }
  .react-resizable-handle-s  { bottom: 0; left: 18px; right: 18px; height: 6px;  width: auto; cursor: s-resize; transform: none; }
  .react-resizable-handle-e  { right: 0;  top: 18px; bottom: 18px; width: 6px; height: auto; cursor: e-resize; transform: none; }
  .react-resizable-handle-w  { left: 0;   top: 18px; bottom: 18px; width: 6px; height: auto; cursor: w-resize; transform: none; }
`;

// ── Inietta {{PLACEHOLDER}} nel HTML del widget ──────────────────────────────
function injectData(html, widgetDataJson) {
  if (!widgetDataJson) return html;
  let map;
  try { map = typeof widgetDataJson === "string" ? JSON.parse(widgetDataJson) : widgetDataJson; }
  catch { return html; }
  return html.replace(/\{\{([A-Za-z0-9_]+)\}\}/g, (m, k) =>
    Object.prototype.hasOwnProperty.call(map, k) ? JSON.stringify(map[k]) : m
  );
}

function buildDoc(widget) {
  const token = sessionStorage.getItem("portal_token") || "";
  const env   = `<script>window.ENV={API_URL:"${API_URL}",token:"${token}"};<\/script>`;
  return env + "\n" + injectData(widget.widget_content, widget.widget_data);
}

// ── Default layout per widget senza posizione salvata ────────────────────────
function defaultLayout(widgets) {
  return widgets.map((w, i) => {
    const lc = w.layout_config;
    return {
      i: w.widget_id,
      x: lc?.x ?? (i % 2) * 6,
      y: lc?.y ?? Math.floor(i / 2) * 5,
      w: lc?.w ?? 6,
      h: lc?.h ?? 5,
      minW: 2, minH: 3,
    };
  });
}

// ── Singola card widget ───────────────────────────────────────────────────────
function WidgetCard({ widget, editMode }) {
  const theme = useTheme();
  const [doc, setDoc]            = useState(null);
  const [loadingDoc, setLoading] = useState(false);
  const [expanded, setExpanded]  = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const token = sessionStorage.getItem("portal_token") || "";
      try {
        const res  = await fetch(
          `${API_URL}/api/client/widgets/${encodeURIComponent(widget.widget_id)}/content`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (res.ok && !cancelled) setDoc(buildDoc(data));
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [widget.widget_id]);

  return (
    <>
      <Box
        sx={{
          width: "100%", height: "100%",
          position: "relative",
          borderRadius: editMode ? 2 : 1,
          border: editMode
            ? `2px dashed ${alpha(theme.palette.primary.main, 0.5)}`
            : "none",
          overflow: "hidden",
          bgcolor: "background.paper",
          transition: "border 0.2s",
          boxShadow: editMode ? "none" : "0 1px 4px rgba(0,0,0,0.08)",
        }}
      >
        {/* Contenuto iframe —
            In edit mode pointer-events:none così l'iframe non cattura il mouse
            e tutti gli handle di resize + drag sono accessibili liberamente. */}
        {loadingDoc ? (
          <Box sx={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%" }}>
            <CircularProgress size={22} />
          </Box>
        ) : doc ? (
          <iframe
            title={widget.title}
            srcDoc={doc}
            style={{
              width:"100%", height:"100%", border:"none", display:"block",
              pointerEvents: editMode ? "none" : "auto",
            }}
            sandbox="allow-scripts allow-same-origin allow-forms"
          />
        ) : (
          <Box sx={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", color:"text.disabled" }}>
            <Typography variant="caption">Nessun contenuto</Typography>
          </Box>
        )}

        {/* ── EDIT MODE ────────────────────────────────────────────────────── */}
        {editMode && (
          <>
            {/* Barra titolo — è il drag handle principale.
                pointer-events:auto per ricevere eventi, className drag-handle
                per essere riconosciuta da GridLayout draggableHandle. */}
            <Box
              className="drag-handle"
              sx={{
                position: "absolute", top: 0, left: 0, right: 0,
                zIndex: 2,
                display: "flex", alignItems: "center",
                bgcolor: alpha(theme.palette.background.paper, 0.92),
                backdropFilter: "blur(6px)",
                px: 1, py: 0.4,
                gap: 0.5,
                cursor: "grab",
                "&:active": { cursor: "grabbing" },
                // Lascia spazio al bottone espandi (40px a destra)
                pr: "36px",
              }}
            >
              <DragIndicator sx={{ fontSize: 15, color: "text.disabled", flexShrink: 0 }} />
              <Typography variant="caption" fontWeight={600} noWrap sx={{ flex: 1, fontSize: 11 }}>
                {widget.title}
              </Typography>
            </Box>

            {/* Bottone espandi — classe no-drag per non triggerare il drag */}
            <Stack
              className="no-drag"
              direction="row"
              sx={{ position: "absolute", top: 2, right: 4, zIndex: 3 }}
            >
              <Tooltip title="Espandi">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
                  sx={{ p: 0.4 }}
                >
                  <OpenInFull sx={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </>
        )}

        {/* Bottone espandi in view mode */}
        {!editMode && (
          <IconButton
            size="small"
            onClick={() => setExpanded(true)}
            sx={{
              position: "absolute", top: 6, right: 6, zIndex: 2,
              bgcolor: alpha(theme.palette.background.paper, 0.75),
              backdropFilter: "blur(4px)",
              opacity: 0, transition: "opacity 0.2s",
              ".widget-card:hover &": { opacity: 1 },
            }}
          >
            <OpenInFull sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>

      {/* Dialog fullscreen */}
      <Dialog open={expanded} onClose={() => setExpanded(false)} maxWidth="xl" fullWidth
        PaperProps={{ sx: { height: "92vh" } }}>
        <DialogTitle sx={{ display:"flex", alignItems:"center", justifyContent:"space-between", py:1 }}>
          <Typography fontWeight={600}>{widget.title}</Typography>
          <IconButton onClick={() => setExpanded(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: "100%" }}>
          {doc && (
            <iframe title={widget.title} srcDoc={doc}
              style={{ width:"100%", height:"100%", border:"none" }}
              sandbox="allow-scripts allow-same-origin allow-forms"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Pannello laterale personalizzazione ──────────────────────────────────────
function WidgetPanel({ open, onClose, widgets, onToggle }) {
  const theme = useTheme();
  const visibleCount = widgets.filter((w) => w.visible).length;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 320,
          bgcolor: "background.default",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        px: 2.5, py: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>Widget disponibili</Typography>
          <Typography variant="caption" color="text.secondary">
            {visibleCount} di {widgets.length} attivi
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {/* Lista widget */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {widgets.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Nessun widget assegnato
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {widgets.map((widget, idx) => (
              <React.Fragment key={widget.widget_id}>
                {idx > 0 && <Divider component="li" />}
                <ListItem
                  sx={{
                    py: 1.5, px: 2,
                    transition: "background 0.15s",
                    "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={widget.img_icon}
                      alt={widget.title}
                      variant="rounded"
                      sx={{
                        width: 36, height: 36,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: "primary.main",
                        fontSize: 14,
                        fontWeight: 700,
                      }}
                    >
                      {widget.title?.[0]?.toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {widget.title}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color={widget.visible ? "success.main" : "text.disabled"}>
                        {widget.visible ? "Visibile" : "Nascosto"}
                      </Typography>
                    }
                  />
                  <Switch
                    edge="end"
                    checked={!!widget.visible}
                    onChange={() => onToggle(widget)}
                    size="small"
                    color="primary"
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{
        px: 2.5, py: 1.5,
        borderTop: `1px solid ${theme.palette.divider}`,
      }}>
        <Typography variant="caption" color="text.secondary">
          Le modifiche vengono salvate automaticamente.
        </Typography>
      </Box>
    </Drawer>
  );
}

// ── Dashboard principale ──────────────────────────────────────────────────────
export default function PortalDashboard_View({ clientData }) {
  const theme       = useTheme();
  const canvasRef   = useRef(null);
  const canvasWidth = useContainerWidth(canvasRef);
  const [widgets, setWidgets]     = useState([]);
  const [layout,  setLayout]      = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editMode, setEditMode]   = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const saveTimer                 = useRef(null);

  // ── Fetch widget ─────────────────────────────────────────────────────────
  const fetchWidgets = useCallback(async () => {
    const token = sessionStorage.getItem("portal_token");
    if (!token) return;
    try {
      const res  = await fetch(`${API_URL}/api/client/widgets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        const ws = data.widgets || [];
        setWidgets(ws);
        setLayout(defaultLayout(ws));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchWidgets(); }, [fetchWidgets]);

  // ── Salva preferenza singolo widget ─────────────────────────────────────
  const savePreference = useCallback(async (widgetId, visible, position, layoutItem) => {
    const token = sessionStorage.getItem("portal_token") || "";
    const layout_config = layoutItem
      ? { x: layoutItem.x, y: layoutItem.y, w: layoutItem.w, h: layoutItem.h }
      : null;
    try {
      await fetch(`${API_URL}/api/client/widgets/${encodeURIComponent(widgetId)}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ visible, position, layout: layout_config }),
      });
    } catch (e) { console.error(e); }
  }, []);

  // ── Layout change (drag / resize) ────────────────────────────────────────
  const handleLayoutChange = useCallback((newLayout) => {
    setLayout(newLayout);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      newLayout.forEach((item, idx) => {
        const w = widgets.find((w) => w.widget_id === item.i);
        if (w) savePreference(item.i, w.visible, idx, item);
      });
    }, 800);
  }, [widgets, savePreference]);

  // ── Toggle visibilità (dal pannello) ─────────────────────────────────────
  const handleToggleVisible = async (widget) => {
    const newVisible = !widget.visible;
    const updated = widgets.map((w) =>
      w.widget_id === widget.widget_id ? { ...w, visible: newVisible } : w
    );
    setWidgets(updated);
    const lItem = layout.find((l) => l.i === widget.widget_id);
    await savePreference(widget.widget_id, newVisible, widget.position, lItem);
  };

  // Sul canvas mostriamo sempre e solo i widget visibili
  const visibleWidgets = widgets.filter((w) => w.visible);
  const visibleLayout  = layout.filter((l) => visibleWidgets.some((w) => w.widget_id === l.i));

  if (loading) {
    return (
      <Box sx={{ display:"flex", justifyContent:"center", pt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: "calc(100vh - 64px)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {editMode && <style>{RESIZE_HANDLE_CSS}</style>}

      {/* Toolbar */}
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ px: 3, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {clientData?.company_name}
          </Typography>
          {editMode && (
            <Typography variant="caption" color="primary">
              Trascina dal titolo per spostare · Bordi colorati per ridimensionare
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {/* Modifica layout */}
          {visibleWidgets.length > 0 && (
            <Tooltip title={editMode ? "Blocca layout" : "Modifica posizioni"}>
              <IconButton
                onClick={() => setEditMode((v) => !v)}
                color={editMode ? "primary" : "default"}
                sx={{ border: `1px solid ${editMode ? theme.palette.primary.main : "transparent"}` }}
              >
                <EditOutlined />
              </IconButton>
            </Tooltip>
          )}
          {/* Pannello widget */}
          {widgets.length > 0 && (
            <Tooltip title="Widget disponibili">
              <IconButton onClick={() => setPanelOpen(true)} color="default">
                <WidgetsOutlined />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {/* Canvas */}
      <Box
        ref={canvasRef}
        sx={{ flex: 1, overflow: "auto", bgcolor: editMode ? alpha(theme.palette.primary.main, 0.02) : "background.default" }}
      >
        {widgets.length === 0 ? (
          <Box sx={{ display:"flex", justifyContent:"center", pt: 8 }}>
            <Alert severity="info" icon={<Inbox />} sx={{ borderRadius: 3, maxWidth: 500 }}>
              Nessun widget abilitato. Contatta il tuo referente Ecotrade.
            </Alert>
          </Box>
        ) : visibleWidgets.length === 0 ? (
          <Box sx={{ display:"flex", justifyContent:"center", pt: 8 }}>
            <Alert severity="warning" sx={{ borderRadius: 3, maxWidth: 500 }}>
              Nessun widget attivo.{" "}
              <strong style={{ cursor: "pointer" }} onClick={() => setPanelOpen(true)}>
                Clicca qui per attivarne uno.
              </strong>
            </Alert>
          </Box>
        ) : canvasWidth > 0 ? (
          <GridLayout
            layout={visibleLayout}
            width={canvasWidth}
            gridConfig={{
              cols: COLS,
              rowHeight: ROW_H,
              margin: MARGIN,
              containerPadding: [8, 8],
            }}
            dragConfig={{
              enabled: editMode,
              handle: ".drag-handle",
              cancel: ".no-drag",
            }}
            resizeConfig={{
              enabled: editMode,
              handles: ["se", "sw", "ne", "nw", "s", "n", "e", "w"],
            }}
            onLayoutChange={handleLayoutChange}
            style={{ minHeight: "100%" }}
          >
            {visibleWidgets.map((widget) => (
              <div key={widget.widget_id} className="widget-card">
                <WidgetCard
                  widget={widget}
                  editMode={editMode}
                />
              </div>
            ))}
          </GridLayout>
        ) : null}
      </Box>

      {/* Pannello personalizzazione */}
      <WidgetPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        widgets={widgets}
        onToggle={handleToggleVisible}
      />
    </Box>
  );
}
